import { useContext } from 'react';
import { styled } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Field, FormikProvider, FormikValues, useFormik } from "formik";
import * as Yup from 'yup';
import Button from "../../../../customs/Button";
import Input from "../../../../customs/FormInput";
import Select from "../../../../customs/SelectInput";
import { NotificationContext } from "../../../../providers/notification";
import apiCall from "../../../../utils/apiCall";
import Qualification from './Main';
import apiCallFormData from '../../../../utils/apiFormDataCall';

interface ComponentProps {
    specializationName: string;
    handleClose: () => void;
    score: number;
    id: number;
};

const UpdateSpecialization: React.FC<ComponentProps> = ({ specializationName, id, handleClose, score }) => {
    const queryClient = useQueryClient();
    const { showNotification } = useContext(NotificationContext);

    const updateSpecialization = async(data: FormData) => {
        return await (apiCallFormData().put(`/SetUp/UpdateSpecialization?id=${id}`, data));
    };

    const updateSpecializationMutation = useMutation(['update-specialization'], updateSpecialization, {retry: 0});

    const updateQualificationHandler = async (values: FormikValues) => {
        const formData = new FormData();
        formData.append('Id', id.toString());
        formData.append('SpecializationName', values.name ? values?.name : specializationName);
        formData.append('Score', values?.score ? values?.score : score.toString());

        try {
            await updateSpecializationMutation.mutateAsync(formData, {
                onSuccess: () => {
                    showNotification({
                        message: 'Specialization has been successfully updated',
                        type: "success",
                    });

                    queryClient.refetchQueries(['get-specializations']);
                    handleClose();
                }
            })
        }catch(error: any) {
            showNotification({
                message: error?.response?.data?.Message || error?.message,
                type: 'error',
            });
        }
    };

    const validationSchema = Yup.object().shape({
        name: Yup.string().matches(/^[A-Za-z\s!@#$%^&*()/]+$/, 'Only text is allowed').required('required'),
        score: Yup.number().typeError('Only numbers are allowed').required('required')
    });

    const formik = useFormik<FormikValues>({
        initialValues: {
            name: specializationName ? specializationName : '',
            score: score ? score : ''
        },
        onSubmit: (values) => {
            updateQualificationHandler(values);
        },
        validationSchema,
    });

    return (
        <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit}>
                <FormWrapper>
                    <Field 
                        name='name'
                        type='text'
                        as={Input}
                        label='Name'
                        placeholder='Name'
                    />
                    <Field 
                        name='score'
                        type='number'
                        as={Input}
                        label='Score'
                        placeholder='Score'
                    />
                </FormWrapper>
                <ButtonWrapper>
                    <ButtonContainer>
                        <StyledButton onClick={handleClose} text='Cancel'/>
                        <StyledButton 
                            type="submit" 
                            text={updateSpecializationMutation.isLoading ? 'Updating...' : 'Update'}
                            disabled={updateSpecializationMutation.isLoading}
                        />
                    </ButtonContainer>
                </ButtonWrapper>
            </form>
        </FormikProvider>
    )
};

export default UpdateSpecialization;

const FormWrapper = styled('div')`
    margin-inline: 3.5rem;
`

const ButtonWrapper = styled('div')`
    display: flex;
    justify-content: flex-end;
    margin-block: 2.5rem;
    padding-inline: 3.5rem;
`

const ButtonContainer = styled('div')`
    display: flex;
    gap: 2rem;
`

const StyledButton = styled(Button)`
    padding-block: 1rem;
    padding-inline: 2rem;
`

